/**
 * Relatórios → «Seguimento das propostas do site» (HANDOFF S2): o comercial
 * deu seguimento no Odoo às propostas que chegaram? Por comercial (vendedor
 * da lead no Odoo), a lista das que estão sem seguimento (mais atrasadas
 * primeiro) e o indicador principal — % com seguimento dentro do prazo.
 * Lido do Odoo ao abrir e a cada mudança de filtro (GET reporting/followup).
 */
import { useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { ExternalLink, Loader2, RefreshCw } from "lucide-react"
import type { AppShell } from "@/App"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Table, TableBody, TableCell, TableFooter, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import {
  FOLLOWUP_LABEL, FOLLOWUP_VARIANT, buildFollowupReport, formatHours, getFollowupReport,
  type FollowupReportData, type FollowupSellerLine,
} from "@/lib/followup"
import { detailPathForKind, type Kind } from "@/lib/kind-labels"

type Origin = "ai" | "user" | "all"

const pct = (v: number | null) => (v == null ? "—" : `${Math.round(v * 100)}%`)
const int = (n: number) => n.toLocaleString("pt-PT")
const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit" }) : "—")

export function FollowupSection({
  shell, from, to, kind, seller, pageOrigin, load,
}: {
  shell: AppShell | null
  from: string
  to: string
  kind: string | null
  seller: string | null
  /** «Origem da proposta» do topo da página (`null` = Todas). */
  pageOrigin: "ai" | "user" | null
  /** Testes: substitui o pedido ao servidor. */
  load?: (f: { from: string; to: string; kind: string | null; seller: string | null; generated_by: Origin }) => Promise<FollowupReportData>
}) {
  // Por omissão as do site (geradas por IA); segue o filtro do topo quando ele escolhe uma.
  const [origin, setOrigin] = useState<Origin>(pageOrigin ?? "ai")
  useEffect(() => {
    if (pageOrigin) setOrigin(pageOrigin)
  }, [pageOrigin])
  const [data, setData] = useState<FollowupReportData | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [tick, setTick] = useState(0)

  useEffect(() => {
    if (!shell) return
    let cancelled = false
    setLoading(true)
    setError(null)
    const f = { from, to, kind, seller, generated_by: origin }
    ;(load ? load(f) : getFollowupReport(shell, f))
      .then((d) => { if (!cancelled) setData(d) })
      .catch((e) => { if (!cancelled) setError(e instanceof Error ? e.message : "Falha a ler o Odoo") })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [shell, load, from, to, kind, seller, origin, tick])

  const report = useMemo(() => (data ? buildFollowupReport(data.rows) : null), [data])
  const t = report?.totals

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-end justify-between gap-3 pb-2">
        <div>
          <CardTitle className="text-base">Seguimento das propostas do site</CardTitle>
          <p className="mt-1 text-xs text-muted-foreground">
            Lido do Odoo: atividades concluídas, notas do comercial, mudança de etapa ou envio da proposta na app, depois de a
            proposta ser criada. Prazo: a atividade aberta mais antiga ou {data?.hours ?? 48} h desde a criação.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <Label className="text-xs">Propostas</Label>
            <Select value={origin} onValueChange={(v) => setOrigin(v as Origin)}>
              <SelectTrigger className="h-9 min-w-[10rem]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ai">Geradas por IA</SelectItem>
                <SelectItem value="user">Comerciais</SelectItem>
                <SelectItem value="all">Todas</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setTick((n) => n + 1)} disabled={loading}>
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Atualizar
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4 px-0">
        {error && <p className="px-6 text-sm text-destructive">{error}</p>}
        {!report && !error && (
          <div className="flex justify-center py-10 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin" /></div>
        )}
        {report && t && data && (
          <>
            <div className="grid grid-cols-2 gap-3 px-6 md:grid-cols-3 xl:grid-cols-6">
              <Kpi
                label="Com seguimento dentro do prazo"
                value={pct(t.onTime)}
                hint={t.onTimeDen ? `${int(t.onTimeNum)} de ${int(t.onTimeDen)} já decididas` : "nenhuma decidida ainda"}
                strong
              />
              <Kpi label="Sem seguimento" value={int(t.none)} tone={t.none > 0 ? "bad" : undefined} />
              <Kpi label="Pendentes" value={int(t.pending)} hint="dentro do prazo" />
              <Kpi label="Em curso" value={int(t.active)} hint={`1.ª ação em ${formatHours(t.avgHoursToFirst)} (média)`} />
              <Kpi label="Fechadas" value={int(t.closed)} hint="ganhas ou perdidas" />
              <Kpi
                label="Sem lead no Odoo"
                value={int(data.without_lead + t.unknown)}
                hint={t.unknown ? `${int(t.unknown)} com a lead apagada ou sem acesso` : "não entram nas contas"}
              />
            </div>
            {data.rows.some((r) => r.followup.messages_truncated) && (
              <p className="px-6 text-xs text-amber-600">
                {int(data.rows.filter((r) => r.followup.messages_truncated).length)} lead(s) com mais mensagens no Odoo do que a app lê de
                uma vez — o estado dessas pode estar incompleto.
              </p>
            )}
            {data.truncated > 0 && (
              <p className="px-6 text-xs text-amber-600">
                Só as {int(data.rows.length)} propostas mais recentes — ficaram de fora {int(data.truncated)}. Encurte o período.
              </p>
            )}

            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Comercial (Odoo)</TableHead>
                    <TableHead className="text-right">Recebidas</TableHead>
                    <TableHead className="text-right">Sem seguimento</TableHead>
                    <TableHead className="text-right">Pendentes</TableHead>
                    <TableHead className="text-right">Em curso</TableHead>
                    <TableHead className="text-right">Fechadas</TableHead>
                    <TableHead className="text-right">Até à 1.ª ação</TableHead>
                    <TableHead className="text-right">Dentro do prazo</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {report.bySeller.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="py-6 text-center text-sm text-muted-foreground">Sem propostas com lead no Odoo neste período.</TableCell></TableRow>
                  )}
                  {report.bySeller.map((s) => <SellerRow key={s.key} line={s} />)}
                </TableBody>
                {report.bySeller.length > 1 && (
                  <TableFooter><SellerRow line={t} total /></TableFooter>
                )}
              </Table>
            </div>

            <div>
              <h3 className="px-6 pb-2 text-sm font-medium">Sem seguimento <span className="font-normal text-muted-foreground">— as mais atrasadas primeiro</span></h3>
              {report.overdue.length === 0 ? (
                <p className="px-6 pb-2 text-sm text-muted-foreground">Nenhuma proposta com o prazo passado sem seguimento.</p>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Proposta</TableHead>
                        <TableHead>Cliente</TableHead>
                        <TableHead>Comercial</TableHead>
                        <TableHead className="text-right">Atraso</TableHead>
                        <TableHead>Atividade aberta</TableHead>
                        <TableHead className="text-right">Odoo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {report.overdue.map((r) => {
                        const to = detailPathForKind(r.kind as Kind, r.id)
                        const act = r.followup.open_activities[0]
                        return (
                          <TableRow key={r.id}>
                            <TableCell>
                              {to ? <Link to={to} className="font-medium text-accent hover:underline">{r.number}</Link> : <span className="font-medium">{r.number}</span>}
                              <div className="text-xs text-muted-foreground">criada {day(r.created_at)}</div>
                            </TableCell>
                            <TableCell>{r.client_name}</TableCell>
                            <TableCell className="text-muted-foreground">{r.followup.salesperson?.name ?? "—"}</TableCell>
                            <TableCell className="text-right tabular-nums">
                              <Badge variant={FOLLOWUP_VARIANT.none}>{r.followup.overdue_days === 0 ? "< 1 d" : `${r.followup.overdue_days} d`}</Badge>
                            </TableCell>
                            <TableCell className="text-sm">
                              {act ? (
                                <>
                                  <div>{act.summary}</div>
                                  <div className={act.overdue ? "text-xs text-red-600" : "text-xs text-muted-foreground"}>
                                    prazo {act.deadline ? day(act.deadline) : "—"}{act.user ? ` · ${act.user}` : ""}
                                  </div>
                                </>
                              ) : (
                                <span className="text-muted-foreground">Nenhuma</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              {r.followup.odoo_url ? (
                                <a href={r.followup.odoo_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-accent hover:underline">
                                  Lead <ExternalLink className="h-3 w-3" />
                                </a>
                              ) : "—"}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
            <p className="px-6 text-xs text-muted-foreground">
              «{FOLLOWUP_LABEL.pending}»: nada feito, ainda dentro do prazo. «Dentro do prazo» conta as que já se decidiram — com ação
              (a tempo ou não) e as sem seguimento. Notas e etapas mudadas pelo utilizador da integração (envios automáticos) não contam.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function SellerRow({ line: s, total }: { line: FollowupSellerLine; total?: boolean }) {
  return (
    <TableRow>
      <TableCell className={total ? "font-semibold" : "font-medium"}>{s.name}</TableCell>
      <TableCell className="text-right tabular-nums">{int(s.received)}</TableCell>
      <TableCell className={s.none > 0 ? "text-right font-medium tabular-nums text-red-600" : "text-right tabular-nums"}>{int(s.none)}</TableCell>
      <TableCell className="text-right tabular-nums">{int(s.pending)}</TableCell>
      <TableCell className="text-right tabular-nums">{int(s.active)}</TableCell>
      <TableCell className="text-right tabular-nums">{int(s.closed)}</TableCell>
      <TableCell className="text-right tabular-nums">{formatHours(s.avgHoursToFirst)}</TableCell>
      <TableCell className="text-right tabular-nums">{pct(s.onTime)}</TableCell>
    </TableRow>
  )
}

function Kpi({ label, value, hint, strong, tone }: { label: string; value: string; hint?: string; strong?: boolean; tone?: "bad" }) {
  return (
    <div className={strong ? "rounded-lg border border-foreground/30 bg-card p-3" : "rounded-lg border border-border bg-card p-3"}>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums tracking-tight${tone === "bad" ? " text-red-600" : ""}`}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-muted-foreground">{hint}</div>}
    </div>
  )
}
